/**
 * Legacy URL redirect map for SEO preservation after WordPress → Lovable migration.
 *
 * Keys: legacy pathname, lowercase, NO trailing slash, NO query string.
 * Values: final destination pathname on the new site (single-hop, always 200).
 *
 * Only includes legacy URLs that do NOT already exist as real routes on the new
 * site — existing routes are preserved (per migration policy).
 *
 * Used by src/routes/__root.tsx#beforeLoad to issue an SSR 301.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  // --- Service pages ---
  "/foam-roof": "/foam-roofing",
  "/polyurethane-foam-roofing": "/foam-roofing",
  "/flat-roof": "/foam-roofing",
  "/elastomeric-coatings": "/foam-roof-coating-recoating",
  "/shingle-roof-phoenix": "/shingle-roofing",
  "/leak-detection": "/roof-leak-repair",
  "/phoenix-roof-repair": "/roof-repair",
  "/phoenix-roof-leak-repair": "/roof-leak-repair",
  "/roof-replacement": "/services",
  "/free-estimate": "/contact-us",
  "/scottsdale": "/scottsdale-roofing-contractor",
  "/skylight-leaks-when-it-rains": "/skylight-repair",

  // --- Blog/content posts (improved targets vs generic /blog) ---
  "/is-foam-roofing-worth-it": "/foam-roofing",
  "/why-are-there-bubbles-in-my-flat-roof": "/blog/foam-roof-bubbles-causes-repair-phoenix",
  "/blog/foam-roof-bubbles-in-phoenix": "/blog/foam-roof-bubbles-causes-repair-phoenix",
  "/contact-us.html": "/contact-us",
  "/what-is-the-best-roofing-for-arizona-homes": "/foam-roofing",
  "/how-long-does-elastomeric-roof-coating-last": "/foam-roof-coating-recoating",
  "/signs-of-a-bad-roofing-job": "/roof-maintenance",
  "/how-long-do-roofs-last-on-houses": "/foam-roof-energy-efficiency-r-value",

  // --- Legacy .html paths (most have no matching new route) ---
  "/shingle-roof-phoenix.html": "/shingle-roofing",
  "/flat-roof.html": "/foam-roofing",
  "/roof-replacement.html": "/services",
  "/polyurethane-foam-roofing.html": "/foam-roofing",
  "/free-estimate.html": "/contact-us",
  "/phoenix-residential-roofing.html": "/phoenix-residential-roofing",
  "/commercial-roofing.html": "/commercial-roofing",
  "/leak-detection.html": "/roof-leak-repair",
  "/site/foam-roof.html": "/foam-roofing",
  "/how-long-do-roofs-last-on-houses.html": "/foam-roof-energy-efficiency-r-value",
  "/foam-roofs-over-your-garage-the-ultimate-insulation-solution.html":
    "/foam-roofs-over-your-garage-the-ultimate-insulation-solution",
  "/signs-of-a-bad-roofing-job.html": "/roof-maintenance",
  "/how-long-does-elastomeric-roof-coating-last.html": "/foam-roof-coating-recoating",
  "/what-is-the-best-roofing-for-arizona-homes.html": "/foam-roofing",
  "/why-are-there-bubbles-in-my-flat-roof.html": "/blog/foam-roof-bubbles-causes-repair-phoenix",
  "/pros-and-cons-of-solar-power.html": "/pros-and-cons-of-solar-power",
  "/skylight-leaks-when-it-rains.html": "/skylight-repair",
  "/is-foam-roofing-worth-it.html": "/foam-roofing",
  "/difference-between-flat-and-pitched-roof.html": "/difference-between-flat-and-pitched-roof",
  "/residential-foam-roof.html": "/residential-foam-roof",
};

/**
 * Look up a redirect destination for a given pathname.
 * Returns null when no redirect should fire (the URL is either unknown or
 * served by a real route on the new site).
 */
export function getRedirectFor(pathname: string): string | null {
  if (!pathname) return null;
  // Normalize: strip trailing slash (except root) and lowercase.
  let key = pathname.toLowerCase();
  if (key.length > 1 && key.endsWith("/")) key = key.replace(/\/+$/, "");
  return LEGACY_REDIRECTS[key] ?? null;
}
